import lingkLogo from '@/assets/lingk-logo.svg';

import { useEffect, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import {
  Logout as LogoutIcon,
  Menu as MenuIcon,
  OpenInNew as OpenInNewIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';

import { trackLogout } from '@/utils/analytics';
import { logoutElevio } from '@/utils/elevio';
import { shutdownIntercom } from '@/utils/intercom';

import { useHeaderBreadcrumb } from './HeaderBreadcrumbContext';

interface HeaderProps {
  /** Custom header color based on tenant edition */
  color?: string;
  /** Current tenant name for breadcrumb */
  tenantName?: string;
  /** Current tenant ID for breadcrumb navigation */
  tenantId?: string;
  /** Current app name for breadcrumb */
  appName?: string;
  /** Current app ID for breadcrumb navigation */
  appId?: string;
  /** Called when menu button is clicked */
  onMenuClick?: () => void;
}

const HEADER_HEIGHT = 50;
const DEFAULT_COLOR = '#6C63FF';

/**
 * Main application header with user menu
 */
export const Header = ({
  color = DEFAULT_COLOR,
  tenantName,
  tenantId,
  appName,
  appId,
  onMenuClick,
}: HeaderProps) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth0();
  const { contextLabel } = useHeaderBreadcrumb();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { location } = window;

  // Determine if we should show the logo (on home page or special routes)
  const pathArray = location.pathname.split('/');
  const showLogo =
    location.pathname === '/' ||
    pathArray[1] === 'register' ||
    pathArray[1] === 'services' ||
    pathArray[1] === 'datamapping' ||
    pathArray[1] === 'swagger' ||
    pathArray[1] === 'user';

  // Help icons state - load from localStorage
  const [hideHelpIcons, setHideHelpIcons] = useState<boolean>(() => {
    const stored = localStorage.getItem('hideHelpers');
    return stored === 'true';
  });

  // Apply/remove hide-elevio class on mount and when state changes
  useEffect(() => {
    if (hideHelpIcons) {
      document.body.classList.add('hide-elevio');
    } else {
      document.body.classList.remove('hide-elevio');
    }
  }, [hideHelpIcons]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    shutdownIntercom();
    logoutElevio();
    localStorage.removeItem('userToken');
    localStorage.removeItem('profile');
    if (user?.email && user?.sub) {
      trackLogout(user.email, user.sub);
    }
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleSettings = () => {
    handleMenuClose();
    navigate('/account-settings');
  };

  const handleApiExplorer = () => {
    handleMenuClose();
    window.open('https://apiexplorer.lingkapis.com', '_blank');
  };

  const toggleHelpIcons = () => {
    const newValue = !hideHelpIcons;
    setHideHelpIcons(newValue);
    localStorage.setItem('hideHelpers', String(newValue));

    if (newValue) {
      document.body.classList.add('hide-elevio');
    } else {
      document.body.classList.remove('hide-elevio');
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        height: HEADER_HEIGHT,
        backgroundColor: color,
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        sx={{
          minHeight: `${HEADER_HEIGHT}px !important`,
          height: `${HEADER_HEIGHT}px`,
          px: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Menu button (for sidebar toggle) - shown when there's a sidebar */}
        {onMenuClick && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMenuClick}
            sx={{
              mr: 1,
              p: 1,
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Full Logo - shown on home page, special routes, OR when no sidebar/breadcrumbs */}
        {(showLogo || (!onMenuClick && !tenantName && !appName)) && (
          <Box
            onClick={() => navigate('/')}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              mr: 2,
            }}
          >
            <img
              src={lingkLogo}
              alt="Lingk"
              style={{
                height: '30px',
                width: 'auto',
              }}
            />
          </Box>
        )}

        {/* Small Lingk icon - shown when we have breadcrumb (not on home page) */}
        {!showLogo && (tenantName || appName) && (
          <Box
            onClick={() => navigate('/')}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              mr: 1.5,
              height: '100%',
            }}
          >
            <img
              src={lingkLogo}
              alt="Lingk"
              style={{
                height: '24px',
                width: 'auto',
                display: 'block',
              }}
            />
          </Box>
        )}

        {/* Breadcrumb - matches legacy dataProtect: "TENANT ➤ PROJECT ➤ CONTEXT" */}
        {(tenantName || appName) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
            {tenantName && (
              <>
                <Typography
                  variant="body2"
                  onClick={() => {
                    // Navigate to home page filtered by this tenant using query param
                    if (tenantId) {
                      navigate(`/?tenantId=${tenantId}`);
                    } else {
                      navigate('/');
                    }
                  }}
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    cursor: 'pointer',
                    lineHeight: 1,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {tenantName}
                </Typography>
                {(appName || contextLabel) && (
                  <Typography
                    variant="body2"
                    sx={{ color: 'white', fontSize: '1rem', lineHeight: 1 }}
                  >
                    ➤
                  </Typography>
                )}
              </>
            )}
            {appName && (
              <>
                <Typography
                  variant="body2"
                  onClick={() => {
                    if (appId) {
                      navigate(`/a/${appId}/dash`);
                    }
                  }}
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    cursor: appId ? 'pointer' : 'default',
                    lineHeight: 1,
                    '&:hover': appId
                      ? {
                          textDecoration: 'underline',
                        }
                      : {},
                  }}
                >
                  {appName}
                </Typography>
                {contextLabel && (
                  <Typography
                    variant="body2"
                    sx={{ color: 'white', fontSize: '1rem', lineHeight: 1 }}
                  >
                    ➤
                  </Typography>
                )}
              </>
            )}
            {contextLabel && (
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  fontSize: '0.875rem',
                  letterSpacing: '0.5px',
                  lineHeight: 1,
                }}
              >
                {contextLabel}
              </Typography>
            )}
          </Box>
        )}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* User section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'white',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {user?.name || user?.email}
          </Typography>

          <Tooltip title="Account settings">
            <IconButton onClick={handleMenuOpen} sx={{ p: 0.5 }}>
              <Avatar
                src={user?.picture}
                alt={user?.name || 'User'}
                sx={{
                  width: 32,
                  height: 32,
                  border: '2px solid rgba(255,255,255,0.3)',
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>

        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              sx: {
                width: 220,
                mt: 1,
              },
            },
          }}
        >
          <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Account Settings</ListItemText>
          </MenuItem>

          {/* TODO: remove this functionality */}
          {/* <MenuItem onClick={handleApiExplorer}>
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>API Explorer</ListItemText>
          </MenuItem> */}

          {/* Help Icons Toggle - matches legacy dataProtect */}
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              toggleHelpIcons();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 1.5,
              cursor: 'pointer',
            }}
          >
            <Switch
              checked={hideHelpIcons}
              size="small"
              readOnly
              sx={{
                mr: 1.5,
                width: 52,
                height: 26,
                padding: 0,
                pointerEvents: 'none',
                '& .MuiSwitch-switchBase': {
                  padding: 0,
                  margin: '2px',
                  '&.Mui-checked': {
                    transform: 'translateX(26px)',
                    color: '#fff',
                    '& + .MuiSwitch-track': {
                      backgroundColor: '#478CC9',
                      opacity: 1,
                      border: 0,
                      '&::after': {
                        content: '"hide"',
                        position: 'absolute',
                        left: '6px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '10px',
                        fontWeight: 700,
                        color: 'white',
                        textTransform: 'lowercase',
                      },
                    },
                  },
                },
                '& .MuiSwitch-thumb': {
                  width: 22,
                  height: 22,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                },
                '& .MuiSwitch-track': {
                  borderRadius: 26 / 2,
                  backgroundColor: '#478CC9',
                  opacity: 1,
                  position: 'relative',
                  '&::after': {
                    content: '"show"',
                    position: 'absolute',
                    right: '4px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '10px',
                    fontWeight: 700,
                    color: 'white',
                    textTransform: 'lowercase',
                  },
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: '#478CC9',
                fontWeight: 700,
                fontSize: '14px',
              }}
            >
              Help Icons
            </Typography>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sign Out</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};
